<template>
  <ten-popup
    trigger-type="manual"
    popup-class="ten-picker"
    :direction="direction"
    :show="popupShow"
    @change="popupShow = false"
  >
    <!-- input框 -->
    <template v-slot:trigger>
      <div
        :class="triggerClassName"
        @click="onTriggerClick"
      >
        <ten-input-formatted
          :get-input="setInputEle"
          :disabled="disabled"
          :placeholder="isRange ? startPlaceholder : placeholder"
          :size="size"
          :readonly="_readonly"
          :parser="inputValueParser"
          :formatter="inputValueFormatter"
          :value="time[0]"
          :is-changed="isInputValueChanged"
          @change="onInputChange"
          @focus="onInputFocus"
          @keydown="onInputKeydown"
          @blur="onStartInputBlur"
        />
        <!-- input range -->
        <template v-if="isRange">
          <span
            class="ten-picker__input-separator"
            @mousedown.prevent
          >
            {{ rangeSeparator || '至' }}
          </span>
          <ten-input-formatted
            :get-input="el => setInputEle(el, 1)"
            :disabled="disabled"
            :placeholder="endPlaceholder"
            :size="size"
            :readonly="_readonly"
            :parser="v => inputValueParser(v, 1)"
            :formatter="inputValueFormatter"
            :value="time[1]"
            :is-changed="isInputValueChanged"
            @change="v => onInputChange(v, 1)"
            @focus="e => onInputFocus(e, 1)"
            @keydown="onInputKeydown"
            @blur="onEndInputBlur"
          />
        </template>

        <!-- input inline 后缀icon -->
        <div class="ten-picker__input-suffixicon">
          <span class="ten-picker__input-icon ten-picker__input-icon--suffix">
            <!-- @slot 后缀图标 -->
            <slot name="suffixIcon">
              <ten-icon :icon="_suffixIcon" />
            </slot>
          </span>
          <span
            class="ten-picker__input-icon ten-picker__input-icon--clear"
            @click.stop="clear"
          >
            <!-- @slot 清除按钮 -->
            <slot
              v-if="allowClear"
              name="clearIcon"
            >
              <ten-icon :icon="clearIcon || 'circle_clear'" />
            </slot>
          </span>
        </div>
      </div>
    </template>

    <!-- 弹层 -->
    <div
      class="ten-picker__popup"
      @mousedown.prevent
    >
      <!-- shortcut -->
      <div
        v-if="shortCut"
        class="ten-picker__popup-shortcut"
      >
        <ten-button
          v-for="(item, i) in shortCut"
          class="ten-picker__popup-shortcut-btn"
          type="text"
          size="small"
          :key="i"
          @click="shortCutClickHandler(item.value)"
        >
          {{ item.text }}
        </ten-button>
      </div>

      <!-- @slot @ignore panel -->
      <slot v-bind="panelProps" />
    </div>
  </ten-popup>
</template>

<script>
import TenPopup from '../popup';
import TenIcon from '../icon';
import TenButton from '../button';
import valueFormat from '../input/value-format';
import { parseToDate, dateToFormat, isSameTime, isSame, outOfRanges } from './util';
import { isString, isDate } from '../scripts/utils/helper';

const TenInputFormatted = valueFormat();

const DEFAULT_FORMAT = {
  time: 'HH:mm:ss',
  date: 'YYYY-MM-DD',
  datetime: 'YYYY-MM-DD HH:mm:ss',
  month: 'YYYY-MM',
  year: 'YYYY',
};

const name = 'ten-picker';

export default {
  name,
  provide() {
    return {
      picker: this,
    };
  },
  inject: {
    formItem: {
      from: 'tenFormItem',
      default: undefined,
    },
    wrapper: {
      from: 'wrapper',
      default: undefined,
    },
  },
  components: {
    TenInputFormatted,
    TenPopup,
    TenIcon,
    TenButton,
  },
  inheritAttrs: false,
  props: {
    /**
     * 值
     * @model
     */
    value: { type: [Date, String, Array] },
    /**
     * 是否为对应类型的范围选择
     */
    isRange: Boolean,
    /**
     * 显示给用户的格式。如果没传，将取 value-format 的值；如果 value-format 没传，将取组件内定义的默认值。
     */
    format: String,
    /**
     * 绑定值的格式。不指定则绑定值为 Date 类型
     */
    valueFormat: String,
    /**
     * 当 value 为 Date 对象并且指定了 value-format，
     * 强制格式化成 value-format 指定格式，并触发 change 事件更新
     */
    forceValueFormat: { type: Boolean, default: true },
    /**
     * 定制面板的快捷选择方式
     */
    shortCut: Array,
    /**
     * 是否显示清除按钮
     */
    allowClear: { type: Boolean, default: true },
    /**
     * input 框的尺寸，参考 input size 属性
     */
    size: { type: String, default: 'default', validator(v) {
      return ['default', 'small'].indexOf(v) > -1;
    } },
    /**
     * 是否为错误状态
     */
    error: Boolean,
    /**
     * 禁用
     */
    disabled: Boolean,
    /**
     * 只读
     */
    readonly: Boolean,
    /**
     * 非范围选择时的占位内容
     */
    placeholder: {
      type: String,
      default: '请选择',
    },
    /**
     * 范围选择时开始日期的占位内容
     */
    startPlaceholder: {
      type: String,
      default: '开始',
    },
    /**
     * 范围选择时结束日期的占位内容
     */
    endPlaceholder: {
      type: String,
      default: '结束',
    },
    /**
     * 范围选择input框中的连接符
     */
    rangeSeparator: String,
    /**
     * 后缀 icon，支持同名 slot
     */
    suffixIcon: String,
    /**
     * 清除按钮 icon，支持同名 slot
     */
    clearIcon: String,
    /**
     * 面板的弹出方向，参考 popup 的 direction 属性
     */
    direction: { type: String, default: 'bottom left' },
    /**
     * @ignore
     */
    type: { type: String, validator(v) {
      return ['time', 'date', 'datetime', 'month', 'year'].indexOf(v) > -1;
    } },
  },
  data() {
    return {
      time: this.transform(this.value),
      popupShow: false,
      startInputfocused: false,
      endInputfocused: false,
    };
  },
  computed: {
    triggerClassName() {
      return [
        'ten-picker__input',
        `ten-picker__input--size-${this.size}`,
        {
          'ten-date-picker': this.type !== 'time',
          'ten-time-picker': this.type === 'time',
          'ten-picker__input--focused': this.pickerFocused,
          'ten-picker__input--disabled': this.disabled,
          'ten-picker__input--readonly': this.readonly,
          'ten-picker__input--error': this.error,
          'ten-picker__input--empty': !this.time.some(v => v),
          'ten-picker__input--no-clear': !this.allowClear,
        },
      ];
    },
    panelProps() {
      return {
        time: this.isRange ? this.time : this.time[0],
        onChangeHandler: this.onChangeHandler,
        getRelatedInput: this.getRelatedInput,
        format: this._format,
      };
    },
    _readonly() {
      return this.readonly || !this.popupShow;
    },
    pickerFocused() {
      return this.startInputfocused || this.endInputfocused;
    },
    _format() {
      const fmt = this.format;
      return (isString(fmt) && fmt) ? fmt : this._valueFormat;
    },
    _valueFormat() {
      const fmt = this.valueFormat;
      return (isString(fmt) && fmt) ? fmt : DEFAULT_FORMAT[this.type];
    },
    _suffixIcon() {
      return this.suffixIcon || (this.type === 'time' ? 'line_circle_time' : 'calendar');
    },
  },
  watch: {
    value(newValue) {
      this.time = this.transform(newValue);
    },
    popupShow() {
      this.$emit('popupShowChange', this.popupShow);
    },
  },
  beforeCreate() {
    this.inputEle = [];
  },
  methods: {
    transform(value) {
      let time = value;
      const emptyValue = [];

      if (!this.isRange) {
        time = [value];
      } else if (!Array.isArray(value)) {
        time = emptyValue;
      }

      let forceChange = false;
      time = time.map((v) => {
        if (v instanceof Date) {
          forceChange = this.valueFormat && this.forceValueFormat;
        } else if (isString(v)) {
          const fmt = (isString(this.valueFormat) && this.valueFormat) ? this.valueFormat : DEFAULT_FORMAT[this.type];
          v = parseToDate(v, fmt); // data初始化时 this._valueFormat计算属性拿不到
        }

        return isDate(v) ? v : undefined;
      });

      if (forceChange) {
        this.onChangeHandler(time);
        return emptyValue;
      }
      return time;
    },

    reverse(value) {
      const { isRange, valueFormat } = this;
      if (isString(valueFormat)) {
        value = value.map(v => dateToFormat(v, this._valueFormat));
      }
      return isRange ? value : value[0];
    },

    onChangeHandler(newTime, hidePopup) {
      hidePopup = hidePopup || this.type.indexOf('time') === -1;

      if (hidePopup) {
        this.$nextTick(() => {
          this.popupShow = false;
        });
      }

      if (this.inputEle[this.lastFocusedInput]) {
        this.inputEle[this.lastFocusedInput].focus();
      }

      // 兼容panel组件参数
      if (!Array.isArray(newTime)) {
        newTime = [newTime];
      }

      // 非受控组件，props没有设置value TODO: 使用map-props
      if (typeof this.$props.value === 'undefined') {
        this.time = newTime;
      }

      this.wrapper && this.wrapper.$emit('change', this.reverse(newTime));
      this.formItem && this.formItem.$emit('form.change');
    },

    onTriggerClick() {
      if (
        this.readonly
        || this.disabled
        || this.popupShow
      ) {
        return;
      }
      this.popupShow = !this.popupShow;
    },

    clear() {
      if (!this.allowClear) return;
      this.onChangeHandler(this.reverse([]), true);
    },

    setInputEle(el, index = 0) {
      this.inputEle[index] = el;
    },

    onInputChange(value, index = 0) {
      const newTime = this.time.slice(0);
      newTime[index] = value;
      this.onChangeHandler(newTime, true);
    },

    onInputFocus(e, index = 0) {
      if (!this.startInputfocused && !this.endInputfocused && this.wrapper) {
        this.wrapper.$emit('focus', e);
      }
      this.lastFocusedInput = index;
      this[index === 0 ? 'startInputfocused' : 'endInputfocused'] = true;
      this.isBluring = false;
    },

    onInputKeydown(e) {
      const keycode = e.keyCode;
      if (
        !this.popupShow
        && (
          keycode === 13
          || keycode === 38
          || keycode === 40
        )
      ) {
        this.popupShow = true;
      }
    },

    onStartInputBlur(e) {
      if (this.isRange) { // 范围选择时不需要延迟
        this.startInputfocused = false;
      }
      this.inputBlurHandler(e);
    },

    onEndInputBlur(e) {
      this.endInputfocused = false;
      this.inputBlurHandler(e, 1);
    },

    inputBlurHandler() {
      this.isBluring = true;

      setTimeout(() => {
        if (this.isBluring) {
          if (!this.isRange) {
            this.startInputfocused = false;
          }

          this.popupShow = false;
          this.wrapper && this.wrapper.$emit('blur');
          this.formItem && this.formItem.$emit('form.blur');
        }
      }, 300);
    },

    inputValueFormatter(date) {
      return dateToFormat(date, this._format);
    },

    inputValueParser(dateStr, index = 0) {
      // 输入框清空
      if (dateStr === '') return;

      let value = parseToDate(dateStr, this._format);

      // fecha解析失败 尝试使用原生解析
      if (value === null) {
        // 兼容不写年份
        if (!dateStr.match(/\d{4}/)) {
          dateStr = `${new Date().getFullYear()} ${dateStr}`;
        }
        value = new Date(dateStr);
      }

      // 验证输入是否为合法值：非空Date、并且在指定范围内，超出范围使用边界值，否则使用上一次的值
      if (
        !isDate(value)
        || this.isInputValueDisabled(value)
      ) {
        const minDate = this.$attrs['min-date'];
        const maxDate = this.$attrs['max-date'];
        if (minDate && value < minDate) return minDate;
        if (maxDate && value > maxDate) return maxDate;

        return this.time[index];
      }

      return value;
    },

    isInputValueDisabled(value) {
      const disabledDate = this.$attrs['disabled-date'];

      return (disabledDate && disabledDate(value))
        || outOfRanges(value, this.$attrs['min-date'], this.$attrs['max-date']);
    },

    isInputValueChanged(newValue, oldValue) {
      const { type } = this;
      if (isDate(oldValue) && isDate(newValue)) {
        switch (type) {
          case 'year':
          case 'month':
          case 'date':
            return !isSame(oldValue, newValue, type);
          case 'time':
            return !isSameTime(oldValue, newValue);
          default:
            return !isSame(oldValue, newValue) || !isSameTime(oldValue, newValue);
        }
      }
      return oldValue !== newValue;
    },

    getRelatedInput(index = 0) {
      return this.inputEle[index];
    },

    shortCutClickHandler(value) {
      if (typeof value === 'function') {
        value = value();
      }
      if (!Array.isArray(value)) {
        value = [value];
      }

      value = value.map((v) => {
        let newV = v;
        if (isString(v)) {
          newV = parseToDate(v, this._valueFormat);
        }

        if (newV === null) {
          newV = new Date(v);
        }

        if (!isDate(newV)) {
          throw new Error(`ten-date-picker: shortcut value: ${v} is invalid date`);
        }

        return newV;
      });

      this.onChangeHandler(value, true);
    },
  },
};
</script>
